/**
 * Welcome / "not connected" screen.
 *
 * The add-in is a companion to the Ezylex CLM web app — its features operate
 * on a contract that was opened in Word *from* Ezylex CLM (via WOPI), which
 * supplies the contract context. When the add-in is instead launched
 * standalone — sideloaded in Word on the desktop or on the web against an
 * arbitrary/blank document — there is no contract context and every feature
 * would otherwise show an error.
 *
 * This screen replaces that broken state with a branded landing page that
 * explains what Ezylex does and how to open a contract so the assistant
 * loads. It is also the surface Microsoft AppSource reviewers see when they
 * sideload the manifest, so it must look finished and self-explanatory.
 */
import { FileText, Sparkles, ShieldCheck, ClipboardList, ArrowUpRight } from 'lucide-react';

const CLM_URL = 'https://app.ezylex.ai';
const SUPPORT_URL = 'https://ezylex.ai/support';

function openExternal(url: string): void {
  try {
    // Office taskpanes block plain target=_blank navigation; the Office
    // dialog/browser API is the supported way to open an external link.
    const officeUi = (typeof Office !== 'undefined' &&
      (Office as any)?.context?.ui) || null;
    if (officeUi?.openBrowserWindow) {
      officeUi.openBrowserWindow(url);
      return;
    }
  } catch {
    /* fall through to window.open */
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

const FEATURES: Array<{ Icon: typeof Sparkles; title: string; desc: string }> = [
  {
    Icon: Sparkles,
    title: 'AI redlining & chat',
    desc: 'Ask for changes in plain language and apply them as tracked redlines.',
  },
  {
    Icon: ShieldCheck,
    title: 'Risk analysis',
    desc: 'Automatically surface risky clauses and off-market terms with severity.',
  },
  {
    Icon: ClipboardList,
    title: 'Playbook & obligations',
    desc: 'Check the contract against your playbook and extract key obligations.',
  },
  {
    Icon: FileText,
    title: 'Contract summary',
    desc: 'Generate a plain-language overview of parties, dates, and value.',
  },
];

export function WelcomeScreen() {
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'var(--bg-soft)', fontFamily: 'var(--font-ui)' }}
    >
      <div className="flex-1 overflow-y-auto px-5 py-6">
        {/* Brand */}
        <div className="flex items-center gap-2 mb-5">
          <img
            src="/logo.png"
            alt="Ezylex.ai — Intelligent Solutions"
            className="h-9 w-auto"
            style={{ maxWidth: 210 }}
          />
          <span
            className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded self-start mt-1"
            style={{ background: 'var(--brand-tint)', color: 'var(--brand-3)' }}
          >
            CLM
          </span>
        </div>

        {/* Hero */}
        <h1 className="text-[19px] font-bold text-[var(--ink)] leading-snug mb-1.5">
          Welcome to Ezylex AI CLM
        </h1>
        <p className="text-[13px] text-[var(--ink-2)] leading-relaxed mb-5">
          Your AI contract assistant for Microsoft Word — redlining, risk
          analysis, playbook checks, obligations, and summaries, right beside
          your document.
        </p>

        {/* Get started card */}
        <div className="ez-card p-4 mb-5">
          <p className="text-[12px] text-[var(--ink-2)] leading-relaxed mb-3">
            The Ezylex AI CLM add-in is a companion to the Ezylex CLM platform.
            It activates automatically when you open a contract from your Ezylex
            workspace.
          </p>

          <p className="text-[12px] font-semibold text-[var(--ink)] mb-2">
            To get started:
          </p>
          <ol className="space-y-2.5 mb-4">
            {[
              <>Sign in to Ezylex CLM at <b>app.ezylex.ai</b>.</>,
              <>Open <b>Contracts</b> and select the contract you want to work on.</>,
              <>Run the <b>Playbook</b> on that contract.</>,
              <>Click <b>Edit in Word</b> (or <b>Edit in Word &amp; Apply Redlines</b>).</>,
              <>The contract opens in Word with this assistant active. Use it to
                review redlines, analyze risk, and ask the AI for changes.</>,
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span
                  className="flex-shrink-0 w-5 h-5 rounded-full grid place-items-center text-white text-[11px] font-bold"
                  style={{ background: 'var(--brand-3)' }}
                >
                  {i + 1}
                </span>
                <span className="text-[12.5px] text-[var(--ink-2)] leading-relaxed">
                  {step}
                </span>
              </li>
            ))}
          </ol>

          <p className="text-[11px] text-[var(--ink-3)] leading-relaxed mb-3">
            An active Ezylex CLM account is required. Signing up from within the
            add-in is not supported.
          </p>

          <button
            type="button"
            onClick={() => openExternal(CLM_URL)}
            className="w-full px-4 py-2.5 rounded-[var(--r-lg)] text-white flex items-center justify-center gap-1.5 transition-opacity hover:opacity-90"
            style={{ fontSize: '13px', fontWeight: 600, background: 'var(--brand)' }}
          >
            Open Ezylex CLM
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>

        {/* Feature highlights */}
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--ink-3)] mb-2.5">
          What you can do
        </p>
        <div className="grid grid-cols-1 gap-2.5 mb-5">
          {FEATURES.map(({ Icon, title, desc }) => (
            <div key={title} className="ez-card p-3 flex items-start gap-3">
              <div
                className="flex-shrink-0 w-8 h-8 rounded-lg grid place-items-center"
                style={{ background: 'var(--brand-tint)' }}
              >
                <Icon className="w-4 h-4" style={{ color: 'var(--brand-3)' }} />
              </div>
              <div>
                <p className="text-[12.5px] font-semibold text-[var(--ink)]">{title}</p>
                <p className="text-[11.5px] text-[var(--ink-3)] leading-snug mt-0.5">
                  {desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Support footer */}
        <p className="text-[11.5px] text-[var(--ink-3)] text-center">
          Need help?{' '}
          <button
            type="button"
            onClick={() => openExternal(SUPPORT_URL)}
            className="underline hover:no-underline"
            style={{ color: 'var(--brand-3)' }}
          >
            Contact support
          </button>
        </p>
      </div>
    </div>
  );
}
